<script setup>
import AudioPlayer from '~/components/audio/AudioPlayer'
import AudioControls from '~/components/audio/AudioControls'
import AudioProgressBar from '~/components/audio/AudioProgressBar'
import AudioPlayerMiniTitle from '~/components/audio/AudioPlayerMiniTitle'
import ChaptersList from '~/components/audio/ChaptersList'
import BaseLazyImage from '~/components/BaseLazyImage'
import { useBook } from '~/composables/useBook'

const defaultProps = defineProps({
  book: {
    type: Object,
    required: true,
  },
})

const { genreToKey } = useBook()
const genre = genreToKey(defaultProps.book.genres)
const chaptersListOpen = ref(false)

function toggleChaptersList() {
  chaptersListOpen.value = !chaptersListOpen.value
}

function onError() {
  chaptersListOpen.value = false
}
</script>

<template>
  <AudioPlayer
    :book="book"
    :genre="genre"
    should-request-progress
    :on-error-callback="onError"
  >
    <template #default="props">
      <div
        :class="[
          'audio-player-mini position-relative',
          { 'audio-player-mini--chapters-open': chaptersListOpen },
        ]"
      >
        <BaseLazyImage
          :src="`${book.cover}&width=132&height=200&fit=cover`"
          :width="66"
          :height="100"
          fluid
        />

        <div class="side-margin--top">
          <AudioPlayerMiniTitle
            :active-chapter="props.activeChapter"
            :current-time="props.currentTime"
            :duration="props.duration"
            :book-title="book.title"
            :chapters="props.chapters"
            :toggle-chapters-list="toggleChaptersList"
            :chapters-list-open="chaptersListOpen"
          />

          <AudioProgressBar
            :value="props.progressPerc"
            :range-input-bg="props.rangeInputBg"
            :genre="genre"
            @change="props.onRangeChange"
            @input="props.onRangeInput"
          />
        </div>

        <ChaptersList
          v-if="chaptersListOpen"
          :chapters="props.chapters"
          :get-audiobook-details="props.getAudiobookDetails"
          :is-loading="props.isLoadingChapters"
          :active-chapter="props.activeChapter"
          :is-playing="props.isPlaying"
          :go-to="props.goTo"
          :pause="props.pause"
          :genre="genre"
        />

        <div class="side-margin--bottom">
          <AudioControls
            :is-prev-enabled="props.isPrevEnabled"
            :is-next-enabled="props.isNextEnabled"
            :is-playing="props.isPlaying"
            :is-loading="props.isLoading"
            :pause="props.pause"
            :play="props.play"
            :go-to="props.goTo"
            :active-chapter="props.activeChapter"
            :chapters="props.chapters"
          />
        </div>
      </div>
    </template>
  </AudioPlayer>
</template>

<style lang="scss" scoped>
.audio-player-mini {
  background: $white;
  padding: 11px 16px;
  border-radius: 6px;
  min-height: 100px;
}

img {
  display: none;
}

.audio-player-mini--chapters-open {
  .side-margin--bottom {
    margin: 16px 0 5px;
  }
}

@include media-breakpoint-up(md) {
  .audio-player-mini {
    width: 480px;
  }

  .side-margin--top,
  .side-margin--bottom {
    margin-left: 62.5px;
    padding-right: 40px;
  }

  img {
    display: block;
    border-radius: 4px;
    box-shadow: 1px 3px 18px 0px #00000033;
    position: absolute;
    left: 0;
    top: 0;
  }

  .audio-player-mini--chapters-open {
    .side-margin--bottom {
      margin: 16px 0 5px 62.5px;
    }
  }
}

@include media-breakpoint-up(lg) {
  .audio-player-mini {
    width: 125%;
  }

  .side-margin--top,
  .side-margin--bottom {
    padding-right: 22%;
  }
}
</style>
